import { Modify, StringObject } from './GenericInterfaces'
import { Country, Sorting } from './Global'
import { InfoTextType } from './Product'
import { AuthRoleType } from './User'
import { SimplifiedStaticValue } from './Product'
import { PropertyValueType } from './Property'

export const SimpleTaxonomies = ['tags', 'collections'] as const
export type SimpleTaxonomy = (typeof SimpleTaxonomies)[number]
export const RegisteredTaxonomies = ['attributes', 'categories', ...SimpleTaxonomies] as const
export type RegisteredTaxonomy = (typeof RegisteredTaxonomies)[number]

/** Tip: Parameterize K extends RegisteredTaxonomy and use TaxonomicTypeMappings[K] to get the connected object type */
export type TaxonomicTypeMappings = {
  attributes: SimplifiedAttribute
  categories: SimplifiedCategory
  tags: SimplifiedTag
  collections: SimplifiedCollection
}

export interface SuperBaseTopLevelTaxonomicObject extends BaseAttribute, BaseCategory, BaseCollection {}
export interface SuperBaseTaxonomicObject extends Modify<SuperBaseTopLevelTaxonomicObject, { defaultName: DefaultTaxonomicValueType }> {}

export type DefaultTaxonomicValueType = string | number

export interface BaseTaxonomicObject<T extends DefaultTaxonomicValueType = DefaultTaxonomicValueType> {
  defaultName: T
}

// not term
interface BaseTopLevelTaxonomicObject extends BaseTaxonomicObject<string> {
  translateAlphabeticals?: boolean
  exclusiveToCountries?: Country[]
}

interface BaseAttribute extends BaseTopLevelTaxonomicObject {
  showAsFilter?: boolean
  unit?: string
  requiresRole?: AuthRoleType
  sanitizeValues?: boolean
  hasTerms?: boolean
  allowMultipleValues?: boolean
  valueType?: string
  info?: StringObject
  doNotTranslateChildren?: boolean
  standardOptions?: DefaultTaxonomicValueType[]
  disabled?: boolean
}

interface BaseCategory extends BaseTopLevelTaxonomicObject {
  parentId?: string
  generateInfoText?: InfoTextType

  itemGroup?: number // Jeeves mapping
  calendarYear?: number // Jeeves mapping
}

interface BaseCollection extends BaseTopLevelTaxonomicObject {
  excludeFromECommerceSync?: boolean
  relations?: string[]
}

// ----------------
// -- Simplified --
// ----------------
export interface SimplifiedTaxonomicLeafObject extends SimplifiedCategory, SimplifiedCollection, SimplifiedTag {}
export interface SimplifiedSuperTopLevelTaxonomicObject extends SimplifiedTaxonomicLeafObject, SimplifiedAttribute {}
export interface SimplifiedSuperTaxonomicObject<T = DefaultTaxonomicValueType> extends Modify<SimplifiedSuperTopLevelTaxonomicObject, { defaultName: T }> {}

export interface SimplifiedTaxonomicObject<T extends DefaultTaxonomicValueType = DefaultTaxonomicValueType>
  extends Modify<BaseTaxonomicObject<T>, { id: string }> {
  name: string
  matches?: number
}

export interface SimplifiedAttribute
  extends SimplifiedTaxonomicObject<string>,
    Modify<BaseAttribute, { id: string; valueType: PropertyValueType; standardOptions?: Array<SimplifiedTerm | SimplifiedStaticValue> }> {
  terms?: SimplifiedTerm[]
  staticValues?: SimplifiedStaticValue[]
}

export interface SimplifiedCategory extends SimplifiedTaxonomicObject<string>, Modify<BaseCategory, { id: string }> {}

export interface SimplifiedCollection extends SimplifiedTaxonomicObject<string>, BaseCollection {}

export interface SimplifiedTag extends SimplifiedTaxonomicObject<string>, BaseTopLevelTaxonomicObject {}

export interface SimplifiedTerm extends SimplifiedTaxonomicObject {}

// -- Misc --

export interface TaxonomicGroup {
  taxonomy: RegisteredTaxonomy
  data: SimplifiedSuperTopLevelTaxonomicObject[]
  sorting?: Sorting
  termsSorting?: { [id: string]: Sorting }
  parent?: SimplifiedSuperTopLevelTaxonomicObject
}

export type TaxonomiesData = Record<RegisteredTaxonomy, { data: Array<SimplifiedSuperTopLevelTaxonomicObject>; updateKey: number }>
